/**
 * Solution Deployment Service
 *
 * This file handles the deployment preparation and execution logic
 * extracted from SolutionImportModal.
 */

import { FIELD_TYPE_MAPPINGS } from "../utils/solutionImportHelpers";

/**
 * Prepare deployment data from solution components
 */
export const prepareSolutionDeployData = (
  solution: any,
  solutionClientUuid: string,
  variableDefaults: any,
  mapping: any,
  importedJSON: any,
  monitors: any,
  inboundAPIs: any,
  inboundCampaigns: any
) => {
  const missingReferences: string[] = [];
  const references = solution.Components?.reduce((acc: any, comp: any) => {
    if (comp.Type === "project") {
      acc[comp.ComponentUuid] = Object.entries(FIELD_TYPE_MAPPINGS).reduce(
        (external, [fieldkey, fieldvalue]) => {
          external[fieldkey] = Object.entries(mapping[fieldkey] || {}).reduce(
            (acc: any, [key, value]: [string, any]) => {
              const match = value.find(
                (v: any) => v.comp === comp.ComponentUuid
              );
              if (match) {
                const reference = importedJSON.Bag.External[fieldkey][key];
                if (fieldkey === "ReportingTemplateField" && !reference) {
                  missingReferences.push(key);
                }
                const referenceKey =
                  fieldkey === "DataStructure" ||
                  fieldkey === "DataStructureField"
                    ? key
                    : match.field;
                acc[referenceKey] = reference;
              }
              return acc;
            },
            {}
          );

          return external;
        },
        {} as any
      );
    }
    return acc;
  }, {});

  if (missingReferences.length > 0) {
    throw new Error("Missing reporting template field mappings");
  }

  return {
    SolutionClientUuid: solutionClientUuid,
    VariableDefaults: variableDefaults,
    References: references,
    Monitors: monitors,
    InboundAPIs: inboundAPIs,
    InboundCampaigns: inboundCampaigns,
  };
};

/**
 * Initialize monitor configuration for weather alerts
 */
export const initializeWeatherMonitor = (componentUuid: string) => ({
  [componentUuid]: {
    Kind: "weather",
    Priority: "low",
    Status: "draft",
    WeatherAlert: {},
  },
});

/**
 * Initialize monitor configuration for ping monitors
 */
export const initializePingMonitor = (componentUuid: string) => ({
  [componentUuid]: {
    Kind: "ping",
    Priority: "low",
    Status: "draft",
    Method: "selectAnApi",
    MonitorPings: [
      {
        Uuid: "",
        PingKind: "liveness",
        IsActive: true,
        Path: "",
        Timeout: 10,
        SuccessThreshold: 1,
        FailureThreshold: 3,
        SuccessCodes: [200],
        MatchBodyMethod: "ignore",
        MatchBody: "",
        Headers: {},
        FrequencyKind: "interval",
        IntervalValue: 1,
        IntervalUnit: "minute",
        Hours: [],
        Minutes: [],
        WeekDays: [0, 1, 2, 3, 4, 5, 6],
        IgnoreDates: [],
        InitialDelay: 1,
      },
      {
        Uuid: "",
        PingKind: "recovery",
        IsActive: false,
        Path: "",
        Timeout: 10,
        SuccessThreshold: 1,
        FailureThreshold: 3,
        SuccessCodes: [200],
        MatchBodyMethod: "ignore",
        MatchBody: "",
        Headers: {},
        FrequencyKind: "interval",
        IntervalValue: 1,
        IntervalUnit: "minute",
        Hours: [],
        Minutes: [],
        WeekDays: [0, 1, 2, 3, 4, 5, 6],
        OnDates: [],
        InitialDelay: 1,
      },
    ],
  },
});

/**
 * Initialize inbound campaign configuration
 */
export const initializeInboundCampaign = (componentUuid: string) => ({
  [componentUuid]: { Channel: "sms", Inbound: "" },
});
